import {useQuery} from '@tanstack/react-query'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'

import {toast} from 'react-toastify'
import {
  ComissionListSchemaResponse,
  CommissionDetailSchemaResponse,
  RecentTransactionsSchemaResponse,
  Responses,
  StoreContactTitleListSchemaResponse,
  StoreListSchemaResponse,
  StoreSchemaResponse,
  UserDetailSchemaResponse,
} from '../domain'
import {useStore} from '../hook'

import useRequestParams from 'src/feature/bite/hook/request'
import {API} from 'src/feature/bite/domain'
import {useIntl} from 'react-intl'
import {getTimeZone} from 'src/feature/bite/helpers/date'

const endPoints = {
  Invoice: 'Payment/ListByStore',
  Contact: 'Store/GetStoreContact',
  Subscription: 'Subscription/ListByStore',
  Transaction: 'Transaction/ListByStore',
  Terminal: 'Terminal/GetTerminals',
  Optional: 'Store/StoreOptionalList',
  User: 'User/UserListByStore',
  UserDetail: 'User/Detail',
  // Commission: 'Store/StoreCommissionList',
  CommissionDetail: 'Commission/Detail',
  Commission: 'Commission/ListByStore',
  SmsEngagement: 'Store/StoreEngagementSMSList',
  PushEngagement: 'Store/StoreEngagementPushList',
  EmailEngagement: 'Store/StoreEngagementEmailList',
}

const typeOfFilterKey = {
  Invoice: 'Date',
  Contact: 'CreatedDate', // One Page
  Subscription: 'ActivationDate',
  Transaction: 'Date',
  Terminal: 'CreatedOn',
  Optional: 'CreatedOn',
  User: 'CreatedOn',
  UserDetail: 'CreatedOn',
  Commission: 'CreatedOn',
  CommissionDetail: 'CreatedOn',
  SmsEngagement: 'CreatedOn',
  PushEngagement: 'CreatedOn',
  EmailEngagement: 'CreatedOn',
}

export function useGetList() {
  const intl = useIntl()
  const {auth, headers, signOut} = useRequestParams()
  const {listSet, listFilter, listFilterSet} = useStore()

  const userID = auth()?.id

  return useQuery(
    ['store', listFilter],
    async () => {
      const Response = await fetch(`${API}/Store/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      const safeParse = StoreListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = StoreListSchemaResponse.parse(Data)
      listSet(parse.response)
      listFilterSet({
        ...listFilter,
        paging: {
          currentPageNumber: parse.response.paging.currentPageNumber,
          pageSize: parse.response.paging.pageSize,
        },
      })

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(intl.formatMessage({id: 'ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetDetail(storeId: string = '') {
  const router = useNavigate()
  const {detailSet, recentTransactionsSet, updateDetail, tab} = useStore()
  const {auth, headers, errorMessage, signOut} = useRequestParams()

  const userID = auth()?.id
  return useQuery(
    ['storeDetail', storeId],
    async () => {
      if (!storeId) return true

      const Response = await fetch(`${API}/Store/Detail`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          storeId,
          language: headers.Language,
          userID,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()
      const safeParse = StoreSchemaResponse.safeParse(Data)
      const parse = StoreSchemaResponse.parse(Data)

      if (!parse.response.store) {
        router('/error/store')
      }

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      detailSet(parse.response.store)

      const filter = {
        filterList: [],
        orderList: [{type: 2, key: 'Date'}],
        paging: {currentPageNumber: 1, pageSize: 5},
        search: {value: '', keyList: []},
      }

      const RecentTransactionsResponse = await fetch(`${API}/Transaction/ListByStore`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...filter,
          storeId,
          userID: auth()?.id,
          language: headers.Language,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      const RecentTransactions = await RecentTransactionsResponse.json()

      const safeParseRecentTransactions =
        RecentTransactionsSchemaResponse.safeParse(RecentTransactions)

      if (!safeParseRecentTransactions.success) {
        return false
      }

      const RecentTransactionsParse = RecentTransactionsSchemaResponse.parse(RecentTransactions)
      recentTransactionsSet(RecentTransactionsParse.response)

      const ContactResponse = await fetch(`${API}/Store/GetStoreContact`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth()?.id,
          language: headers.Language,
          storeId,
          ...filter,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      const Contact = await ContactResponse.json()

      if (Contact.responseResult.result === false) {
        return false
      }

      updateDetail(Contact.response, 'Contact')

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetTabDetail(storeId: string) {
  const {detail, tab, updateDetail, tabFilter, tabFilterSet} = useStore()
  const {auth, headers, signOut, errorMessage} = useRequestParams()

  const currentPageNumber: number = detail?.[tab]?.paging?.currentPageNumber || 1
  const pageSize: number = detail?.[tab]?.paging?.pageSize || 10

  const apiEndPoint = endPoints[tab]
  const typeOfFilter = typeOfFilterKey[tab]

  const filter = {
    orderList: [{type: 2, key: typeOfFilter}],
    search: {value: '', keyList: []},
    // paging: {currentPageNumber, pageSize},
    ...tabFilter,
  }

  let engagementIndex: number

  if (tab === 'SmsEngagement') {
    engagementIndex = 1
  }
  if (tab === 'PushEngagement') {
    engagementIndex = 3
  }
  if (tab === 'EmailEngagement') {
    engagementIndex = 2
  }

  return useQuery(
    ['storeDetailTab', tab, filter],
    async () => {
      if (
        tab === 'Overview' ||
        tab === 'Engagement' ||
        tab === 'CommissionDetail' ||
        tab === 'UserDetail'
      )
        return true

      const Response = await fetch(`${API}/${apiEndPoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth()?.id,
          language: headers.Language,
          timeZoneOffSet: getTimeZone(),
          storeId,
          ...filter,
          ...(engagementIndex && {engagementType: engagementIndex}),
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      const ResponseSchema = Responses[tab]
      const safeParse = ResponseSchema.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ResponseSchema.parse(Data)

      if (parse.responseResult.result === false) {
        throw new Error(parse.responseResult.exceptionDetail || 'Error')
      }

      if (parse.response.paging) {
        tabFilterSet({
          ...tabFilter,
          paging: {
            currentPageNumber: parse.response.paging.currentPageNumber,
            pageSize: parse.response.paging.pageSize,
          },
        })
      }

      updateDetail(parse.response, tab)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error: any) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetCommissionDetail(storeId: string) {
  const intl = useIntl()
  // let {storeId} = useParams()
  const [searchParams] = useSearchParams()
  const {auth, headers, signOut} = useRequestParams()
  const {commissionListFilter, comissionDetailSet, tabSet} = useStore()

  const userID = auth()?.id

  // if (!storeId) storeId = '996225251718'

  const commissionId = searchParams?.get('commissionId') || ''

  return useQuery(
    ['commission', commissionId],
    async () => {
      if (!commissionId || commissionId === '') {
        tabSet('Overview')
        return true
      }

      const Response = await fetch(`${API}/Commission/Detail`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...commissionListFilter,
          storeId,
          userID,
          commissionId,
          language: headers.Language,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      const safeParse = CommissionDetailSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = CommissionDetailSchemaResponse.parse(Data)

      comissionDetailSet(parse.response)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(intl.formatMessage({id: 'ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetUserDetail(storeId: string) {
  const intl = useIntl()
  // let {storeId} = useParams()
  const [searchParams] = useSearchParams()
  const {auth, headers, signOut} = useRequestParams()
  const {userDetailSet, tabSet} = useStore()

  const userID = auth()?.id

  const id = searchParams?.get('userID') || ''

  return useQuery(
    ['userDetail', id],
    async () => {
      if (!id || id === '') {
        tabSet('Overview')
        return true
      }

      const Response = await fetch(`${API}/User/Detail`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          storeId,
          id,
          userID,
          language: headers.Language,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      const safeParse = UserDetailSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = UserDetailSchemaResponse.parse(Data)

      userDetailSet(parse.response)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(intl.formatMessage({id: 'ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetCommissionList() {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const {auth, headers, signOut} = useRequestParams()
  const {commissionListSet, commissionListFilter, commissionListFilterSet} = useStore()

  const userID = auth()?.id

  const CommissionId = searchParams?.get('commissionId') || ''

  return useQuery(
    ['commissionList', commissionListFilter],
    async () => {
      const Response = await fetch(`${API}/CommissionBracket/ListByCommission`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...commissionListFilter,
          userID,
          CommissionId,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      const safeParse = ComissionListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ComissionListSchemaResponse.parse(Data)
      commissionListSet(parse.response)
      commissionListFilterSet({
        ...commissionListFilter,
        paging: {
          currentPageNumber: parse.response.paging.currentPageNumber,
          pageSize: parse.response.paging.pageSize,
        },
      })

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(intl.formatMessage({id: 'ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetStoreContactTitleList() {
  const {auth, headers, signOut, errorMessage} = useRequestParams()
  const {storeContactTitleListSet} = useStore()

  return useQuery(
    ['storeContactTitleList'],
    async () => {
      const Response = await fetch(`${API}/Store/StoreContactTitleList`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          userID: auth()?.id || '',
          language: headers.Language,
          filterList: [],
          orderList: [],
          paging: {
            currentPageNumber: 1,
            pageSize: 100,
          },
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      const safeParse = StoreContactTitleListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = StoreContactTitleListSchemaResponse.parse(Data)
      storeContactTitleListSet(parse.response.storeContactTitleList)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetContactTitleList(token: {userID: string; token: string}) {
  const {headersWithoutAuth, errorMessage, headers} = useRequestParams()
  const {storeContactTitleListSet} = useStore()

  return useQuery(
    ['storeContactTitleList', token],
    async () => {
      if (!token) return true

      const Response = await fetch(`${API}/Store/StoreContactTitleList`, {
        method: 'POST',
        headers: {
          ...headersWithoutAuth,
          Authorization: `Bearer ${token.token}`,
        },
        body: JSON.stringify({
          userID: token.userID,
          language: headers.Language,
          filterList: [],
          orderList: [],
          paging: {
            currentPageNumber: 1,
            pageSize: 100,
          },
        }),
      })

      const Data = await Response.json()

      const safeParse = StoreContactTitleListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = StoreContactTitleListSchemaResponse.parse(Data)
      storeContactTitleListSet(parse.response.storeContactTitleList)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetIsoList() {
  const intl = useIntl()
  const {auth, headers, signOut} = useRequestParams()
  const {isoListSet} = useStore()

  const userID = auth()?.id

  const listFilter = {
    filterList: [],
    orderList: [{type: 2, key: 'CreatedOn'}],
    paging: {currentPageNumber: 1, pageSize: 1000},
    search: {value: '', keyList: []},
  }

  return useQuery(
    ['isolist', listFilter],
    async () => {
      const Response = await fetch(`${API}/ISO/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({...listFilter, userID, language: headers.Language}),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      isoListSet(Data.response.dataList || [])

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(intl.formatMessage({id: 'ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}
